/**
 * Costing tab — parts cost breakdown from BOM lines and POs, true cost override.
 * 
 * Calculates total parts cost from:
 * 1. BOM lines: part.cost × qty_required (estimated cost from part master)
 * 2. PO lines: unit_price × qty (actual ordered cost, when POs exist)
 * Shows both estimated (from BOM) and actual (from POs) when available.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchBomLines } from '../../../api/bom';
import { fetchPurchaseOrders } from '../../../api/purchasing';
import { fetchJobTimeSummary } from '../../../api/timeTracking';
import { updateJob } from '../../../api/jobs';
import { Button, LoadingSpinner } from '../../common';

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}

export default function CostingTab({ job, onRefresh }) {
  const [bomLines, setBomLines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [timeSummary, setTimeSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTrueCost, setEditingTrueCost] = useState(false);
  const [trueCost, setTrueCost] = useState(job.true_cost || '');
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [bom, po, time] = await Promise.all([
        fetchBomLines(job.id),
        fetchPurchaseOrders({ job: job.id }).catch(() => []),
        fetchJobTimeSummary(job.id).catch(() => null),
      ]);
      setBomLines(bom);
      setOrders(Array.isArray(po) ? po : po?.data || po?.results || []);
      setTimeSummary(time);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSaveTrueCost() {
    setSaving(true);
    try {
      await updateJob(job.id, { true_cost: parseFloat(trueCost) || null });
      setEditingTrueCost(false);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>;
  }

  // --- Calculate estimated parts cost from BOM lines (part.cost × qty_required) ---
  let estimatedPartsCost = 0;
  let linesWithCost = 0;
  let linesWithoutCost = 0;
  const bomCostLines = bomLines.map((line) => {
    const unitCost = parseFloat(line.part_cost) || 0;
    const qty = parseFloat(line.qty_required) || 0;
    const lineTotal = unitCost * qty;
    if (unitCost > 0) {
      linesWithCost++;
      estimatedPartsCost += lineTotal;
    } else {
      linesWithoutCost++;
    }
    return {
      part: line.part_display || '—',
      description: line.part_description || '',
      qty,
      unitCost,
      total: lineTotal,
      hasCost: unitCost > 0,
    };
  });

  // --- Calculate actual parts cost from PO lines (if any POs exist) ---
  const partsCostByVendor = {};
  let totalPOCost = 0;
  (Array.isArray(orders) ? orders : []).forEach((po) => {
    const vendorName = po.vendor_name || po.vendor || 'Unknown';
    if (!partsCostByVendor[vendorName]) partsCostByVendor[vendorName] = { lines: [], total: 0 };
    (po.lines || []).forEach((line) => {
      // Fix: Parse quantities separately to avoid JS truthy bug with "0.0000" strings
      const qtyReceived = parseFloat(line.qty_received) || 0;
      const qtyOrdered = parseFloat(line.qty_ordered) || 0;
      const qty = qtyReceived > 0 ? qtyReceived : qtyOrdered;
      const unitPrice = parseFloat(line.unit_price) || 0;
      const lineCost = unitPrice * qty;
      partsCostByVendor[vendorName].lines.push({
        part: line.part_display || line.part || '—',
        qty,
        unitPrice,
        total: lineCost,
      });
      partsCostByVendor[vendorName].total += lineCost;
      totalPOCost += lineCost;
    });
  });

  const hasPOs = Object.keys(partsCostByVendor).length > 0;
  // Always use BOM-based estimated cost for margin calculation.
  // Part.cost is automatically updated from RFQs and POs via Django signal,
  // so BOM cost reflects current pricing for ALL parts (stock + ordered).
  const displayPartsCost = estimatedPartsCost;
  const laborHours = parseFloat(timeSummary?.total_hours) || 0;
  
  // Per-unit sell price and extended (total) sell price
  const sellPricePerUnit = parseFloat(job.sell_price) || 0;
  const quantity = parseInt(job.quantity) || 1;
  const extSellPrice = sellPricePerUnit * quantity;
  
  // Est. margin based on parts cost only
  const computedMargin = extSellPrice > 0 ? ((extSellPrice - displayPartsCost) / extSellPrice * 100) : 0;

  const isComplete = job.status === 'complete' || job.status === 'archived';

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Est. Parts Cost" value={fmtCurrency(estimatedPartsCost)} subtext={linesWithoutCost > 0 ? `${linesWithoutCost} line${linesWithoutCost > 1 ? 's' : ''} missing cost` : undefined} />
        <SummaryCard label="Sell Price/Unit" value={fmtCurrency(sellPricePerUnit)} subtext={`Qty: ${quantity}`} />
        <SummaryCard label="Ext Sell Price" value={fmtCurrency(extSellPrice)} highlight subtext={quantity > 1 ? `${quantity} × ${fmtCurrency(sellPricePerUnit)}` : 'Total revenue'} />
        <SummaryCard
          label="Est. Margin (Parts)"
          value={`${computedMargin.toFixed(1)}%`}
          valueColor={computedMargin > 20 ? 'text-green-600' : computedMargin > 0 ? 'text-yellow-600' : 'text-red-600'}
          subtext={extSellPrice > 0 ? fmtCurrency(extSellPrice - displayPartsCost) : undefined}
        />
        <SummaryCard label="Labor Hours" value={`${laborHours.toFixed(1)}h`} />
      </div>

      {/* True cost override (only after completion) */}
      {isComplete && (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-foreground">True Cost Override</span>
              <p className="text-xs text-gray-500 mt-0.5">
                Set the actual total cost after job completion for accurate reporting.
              </p>
            </div>
            {editingTrueCost ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  className="w-32 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                  value={trueCost}
                  onChange={(e) => setTrueCost(e.target.value)}
                  autoFocus
                />
                <Button size="sm" loading={saving} onClick={handleSaveTrueCost}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingTrueCost(false)}>Cancel</Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">
                  {fmtCurrency(job.true_cost)}
                </span>
                <Button size="sm" variant="ghost" onClick={() => { setTrueCost(job.true_cost || ''); setEditingTrueCost(true); }}>
                  Edit
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Estimated parts cost from BOM */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">BOM Parts Cost (Estimated)</h3>
          <span className="text-sm font-semibold text-foreground">{fmtCurrency(estimatedPartsCost)}</span>
        </div>
        {bomCostLines.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            No BOM lines.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-1.5">Part</th>
                  <th className="px-3 py-1.5 text-right">Qty</th>
                  <th className="px-3 py-1.5 text-right">Unit Cost</th>
                  <th className="px-3 py-1.5 text-right">Line Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bomCostLines.map((line, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-1.5 text-gray-700">
                      {line.part}
                      {line.description && <span className="text-gray-400 ml-1 text-xs">— {line.description}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right text-gray-500">{line.qty}</td>
                    <td className={`px-3 py-1.5 text-right ${line.hasCost ? 'text-gray-700' : 'text-red-400 italic'}`}>
                      {line.hasCost ? fmtCurrency(line.unitCost) : 'No cost'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium">
                      {line.hasCost ? fmtCurrency(line.total) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Total row */}
        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <span className="text-sm font-semibold text-foreground">Total Estimated</span>
          <div className="text-right">
            <span className="text-sm font-bold text-foreground">{fmtCurrency(estimatedPartsCost)}</span>
            {linesWithoutCost > 0 && (
              <div className="text-[10px] text-red-500">{linesWithoutCost} line{linesWithoutCost > 1 ? 's' : ''} missing cost — use Edit Part to add</div>
            )}
          </div>
        </div>
      </div>

      {/* Actual PO cost breakdown (if any POs exist) */}
      {hasPOs && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-medium text-foreground">Actual PO Cost (by Vendor)</h3>
            <span className="text-sm font-semibold text-foreground">{fmtCurrency(totalPOCost)}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(partsCostByVendor).map(([vendor, data]) => (
              <div key={vendor}>
                <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{vendor}</span>
                  <span className="text-sm font-semibold text-foreground">{fmtCurrency(data.total)}</span>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {data.lines.map((line, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-1.5 text-gray-700">{line.part}</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">{line.qty} units</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">@ {fmtCurrency(line.unitPrice)}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{fmtCurrency(line.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <span className="text-sm font-semibold text-foreground">Total PO Cost</span>
            <span className="text-sm font-bold text-foreground">{fmtCurrency(totalPOCost)}</span>
          </div>
        </div>
      )}

      {/* BOM lines summary */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-500">
        {bomLines.length} BOM lines &middot;{' '}
        {bomLines.filter((l) => l.line_status === 'ready').length} ready &middot;{' '}
        {bomLines.filter((l) => l.line_status === 'partial').length} partial &middot;{' '}
        {bomLines.filter((l) => l.line_status === 'pending').length} pending
      </div>
    </div>
  );
}

function SummaryCard({ label, value, valueColor = 'text-foreground', subtext, highlight = false }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${
      highlight 
        ? 'bg-blue-50 border-blue-200' 
        : 'bg-white border-gray-200'
    }`}>
      <dt className={`text-xs ${highlight ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>{label}</dt>
      <dd className={`text-lg font-semibold ${valueColor}`}>{value}</dd>
      {subtext && <dd className="text-[10px] text-gray-400 mt-0.5">{subtext}</dd>}
    </div>
  );
}
